import type {
  HelpOrderStatus,
  HelpPaymentStatus,
  HelpPricingMode,
  HelpProvider,
  HelpServiceKind,
} from "./types";

type ChipColor = "warning" | "accent" | "success" | "default";

export const HELP_STATUS_LABEL: Record<HelpOrderStatus, string> = {
  awaiting_confirmation: "Ожидает подтверждения",
  en_route: "Исполнитель в пути",
  on_site: "На месте",
  in_progress: "Работы выполняются",
  completed: "Завершён",
  cancelled: "Отменён",
};

export const HELP_STATUS_COLOR: Record<HelpOrderStatus, ChipColor> = {
  awaiting_confirmation: "warning",
  en_route: "accent",
  on_site: "accent",
  in_progress: "accent",
  completed: "success",
  cancelled: "default",
};

export const HELP_STATUS_OPTIONS: HelpOrderStatus[] = [
  "awaiting_confirmation",
  "en_route",
  "on_site",
  "in_progress",
  "completed",
  "cancelled",
];

export const HELP_PAYMENT_LABEL: Record<HelpPaymentStatus, string> = {
  pending: "Ожидает",
  success: "Оплачено",
  refunded: "Возврат",
};

export const HELP_PAYMENT_COLOR: Record<HelpPaymentStatus, ChipColor> = {
  pending: "warning",
  success: "success",
  refunded: "default",
};

export const HELP_PAYMENT_OPTIONS: HelpPaymentStatus[] = [
  "pending",
  "success",
  "refunded",
];

export const NOTIFY_TEMPLATES: Record<HelpOrderStatus, string> = {
  awaiting_confirmation:
    "Ожидайте подтверждения. Диспетчер найдёт специалиста в течение 10 минут.",
  en_route: "Исполнитель выехал. Будет примерно через {eta} минут.",
  on_site:
    "Исполнитель на месте. Позвоните, если не можете найти друг друга.",
  in_progress:
    "Работы выполняются. Если стоимость изменится, специалист согласует её с вами.",
  completed:
    "Заказ завершён. Если есть доплата, оплатите её специалисту на месте.",
  cancelled: "Заявка отменена. Если была предоплата — вернём её.",
};

export const HELP_PROVIDER_LABEL: Record<HelpProvider, string> = {
  internal: "Наше ядро",
  lat: "ЛАТ",
};

export const HELP_PRICING_MODE_LABEL: Record<HelpPricingMode, string> = {
  fixed: "Фикс",
  per_unit: "За единицу",
  flexible: "Гибкая цена",
};

export const HELP_PRICING_MODE_OPTIONS: HelpPricingMode[] = [
  "fixed",
  "per_unit",
  "flexible",
];

export const HELP_SERVICE_KIND_LABEL: Record<HelpServiceKind, string> = {
  tow: "Эвакуация",
  jump_start: "Прикурить",
  fuel: "Подвоз бензина",
};

export const HELP_SERVICE_KIND_OPTIONS: HelpServiceKind[] = [
  "tow",
  "jump_start",
  "fuel",
];

export const HELP_SERVICE_KIND_COMING_SOON: Record<HelpServiceKind, boolean> = {
  tow: false,
  jump_start: true,
  fuel: true,
};

const HELP_DATE_PARTS =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

/**
 * Бэкенд отдаёт уже московские часы, часто с лишним `Z`.
 * `new Date(iso).toLocaleString()` тогда даёт +3 часа.
 * Печатаем цифры из строки, без перевода пояса.
 */
export function parseHelpDate(iso: string): Date {
  const trimmed = iso.trim();
  if (!trimmed) return new Date(Number.NaN);
  if (/^\d{13,}$/.test(trimmed)) return new Date(Number(trimmed));
  if (/^\d{10}$/.test(trimmed)) return new Date(Number(trimmed) * 1000);

  const match = trimmed.match(HELP_DATE_PARTS);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second ?? 0),
    );
  }

  return new Date(trimmed);
}

export function formatHelpDate(iso: string): string {
  if (!iso) return "—";
  const trimmed = iso.trim();
  const match = trimmed.match(HELP_DATE_PARTS);
  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${day}.${month}.${year}, ${hour}:${minute}`;
  }
  const date = parseHelpDate(trimmed);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Короткий id заявки для UI; полный uuid — в title / копирование. */
export function formatHelpOrderShortId(uuid: string): string {
  const normalized = uuid.trim().toLowerCase();
  if (!normalized) return "—";
  const compact = normalized.replace(/-/g, "");
  if (compact.length >= 8) {
    return `№ ${compact.slice(0, 8)}`;
  }
  return `№ ${normalized}`;
}

export function formatHelpMoney(value: number): string {
  return value.toLocaleString("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  });
}

function pluralizeRu(count: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.trunc(count));
  const mod100 = abs % 100;
  const mod10 = abs % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/** «1 год», «24 года», «25 лет» */
export function formatHelpExperienceYears(years: number): string {
  return `${years} ${pluralizeRu(years, "год", "года", "лет")}`;
}
